import { useEffect, useRef, type FC } from 'react';
import { Button, Typography } from 'antd';
import { ArrowRightOutlined, StarFilled } from '@ant-design/icons';
import {
  trackExternalLink,
  trackUpworkCardView,
  trackUpworkCtaClick,
} from '@/utils/analytics';

const { Title, Text } = Typography;

interface UpworkData {
  enabled?: boolean;
  profileUrl?: string;
  rating?: number;
  reviewCount?: number;
  stats?: Array<{ label: string; value: string }>;
  /** deprecated — falls back into stats when no explicit list */
  jobSuccess?: number;
  totalJobs?: string;
  totalEarned?: string;
  badgeEnabled?: boolean;
  badge?: string;
  headline?: string;
  subtitle?: string;
  ctaText?: string;
}

interface UpworkCardProps {
  data?: UpworkData;
}

/** Official Upwork wordmark. Crisp at any size. */
const UpworkLogo: FC<{ size?: number }> = ({ size = 28 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 102 28"
    height={size}
    role="img"
    aria-label="Upwork"
    style={{ display: 'block', color: '#14a800' }}
  >
    <path
      fill="currentColor"
      d="M28.18,19.06A6.54,6.54,0,0,1,23,16c.67-5.34,2.62-7,5.2-7s4.54,2,4.54,5-2,5-4.54,5m0-13.34a7.77,7.77,0,0,0-7.9,6.08,26,26,0,0,1-1.93-5.62H12v7.9c0,2.87-1.3,5-3.85,5s-4-2.12-4-5l0-7.9H.49v7.9A8.61,8.61,0,0,0,2.6,20a7.27,7.27,0,0,0,5.54,2.35c4.41,0,7.5-3.39,7.5-8.24V8.77a25.87,25.87,0,0,0,3.66,8.05L17.34,28h3.72l1.29-7.92a11,11,0,0,0,1.36,1,8.32,8.32,0,0,0,4.14,1.28h.34A8.1,8.1,0,0,0,36.37,14a8.12,8.12,0,0,0-8.19-8.31"
    />
    <path
      fill="currentColor"
      d="M80.8,7.86V6.18H77.2V21.81h3.65V15.69c0-3.77.34-6.48,5.4-6.13V6c-2.36-.18-4.2.31-5.45,1.87"
    />
    <polygon
      fill="currentColor"
      points="55.51 6.17 52.87 17.11 50.05 6.17 45.41 6.17 42.59 17.11 39.95 6.17 36.26 6.17 40.31 21.82 44.69 21.82 47.73 10.71 50.74 21.82 55.12 21.82 59.4 6.17 55.51 6.17"
    />
    <path
      fill="currentColor"
      d="M67.42,19.07c-2.59,0-4.53-2.05-4.53-5s2-5,4.53-5S72,11,72,14s-2,5-4.54,5m0-13.35A8.1,8.1,0,0,0,59.25,14,8.18,8.18,0,1,0,75.6,14a8.11,8.11,0,0,0-8.18-8.31"
    />
    <path
      fill="currentColor"
      d="M91.47,14.13h.84l5.09,7.69h4.11l-5.85-8.53a7.66,7.66,0,0,0,4.74-7.11H96.77c0,3.37-2.66,4.65-5.3,4.65V0H87.82V21.82h3.64Z"
    />
  </svg>
);

const formatNumber = (n?: number): string => {
  if (typeof n !== 'number') return '—';
  return n % 1 === 0 ? n.toFixed(1) : n.toString();
};

export const UpworkCard: FC<UpworkCardProps> = ({ data }) => {
  if (!data || data.enabled === false) {
    return null;
  }

  const {
    profileUrl,
    rating = 5,
    reviewCount,
    stats,
    jobSuccess,
    totalJobs,
    totalEarned,
    badgeEnabled,
    badge = 'Top Rated',
    headline = 'Excellence delivered, project after project',
    subtitle = 'Verified results from clients I have worked with on Upwork',
    ctaText = 'View my Upwork profile',
  } = data;

  // Prefer the new `stats` array; fall back to the deprecated individual
  // fields so older JSON content keeps rendering.
  const resolvedStats: Array<{ label: string; value: string }> =
    stats && stats.length > 0
      ? stats.filter((s) => s && (s.label || s.value))
      : [
          ...(typeof jobSuccess === 'number'
            ? [{ label: 'Job Success', value: `${jobSuccess}%` }]
            : []),
          ...(totalJobs ? [{ label: 'Jobs completed', value: totalJobs }] : []),
          ...(totalEarned ? [{ label: 'Total earned', value: totalEarned }] : []),
        ];

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating - fullStars >= 0.5;

  const handleClick = () => {
    if (profileUrl) {
      trackUpworkCtaClick(profileUrl);
      trackExternalLink(profileUrl);
    }
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const viewFiredRef = useRef(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || viewFiredRef.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      viewFiredRef.current = true;
      trackUpworkCardView();
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewFiredRef.current) {
          viewFiredRef.current = true;
          trackUpworkCardView();
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className="upwork-card" ref={cardRef}>
      <div className="upwork-card__bg" aria-hidden="true" />

      <div className="upwork-card__inner">
        <header className="upwork-card__header">
          <div className="upwork-card__brand">
            <UpworkLogo size={32} />
            {badgeEnabled !== false && badge && (
              <span className="upwork-card__badge">
                <span className="upwork-card__badge-dot" />
                {badge}
              </span>
            )}
          </div>
        </header>

        <div className="upwork-card__title-block">
          <Title
            level={3}
            style={{
              marginTop: 0,
              marginBottom: 8,
              fontSize: 'clamp(22px, 3vw, 28px)',
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
            }}
          >
            {headline}
          </Title>
          {subtitle && (
            <Text style={{ fontSize: 15, opacity: 0.7, lineHeight: 1.6 }}>
              {subtitle}
            </Text>
          )}
        </div>

        <div className="upwork-card__rating">
          <div className="upwork-card__stars" aria-label={`${rating} out of 5 stars`}>
            {Array.from({ length: 5 }).map((_, i) => {
              const filled = i < fullStars;
              const half = !filled && hasHalfStar && i === fullStars;
              return (
                <StarFilled
                  key={i}
                  className={`upwork-card__star${filled ? ' is-filled' : ''}${half ? ' is-half' : ''}`}
                />
              );
            })}
          </div>
          <span className="upwork-card__rating-value">{formatNumber(rating)}</span>
          {typeof reviewCount === 'number' && reviewCount > 0 && (
            <span className="upwork-card__review-count">
              ({reviewCount.toLocaleString()} reviews)
            </span>
          )}
        </div>

        {resolvedStats.length > 0 && (
          <div className="upwork-card__stats">
            {resolvedStats.map((stat, i) => (
              <div key={`${stat.label}-${i}`} className="upwork-card__stat">
                <span className="upwork-card__stat-value">{stat.value}</span>
                <span className="upwork-card__stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        )}

        {profileUrl && (
          <div className="upwork-card__cta">
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              className="upwork-card__link"
            >
              <Button
                type="primary"
                className="upwork-card__button"
                style={{ fontWeight: 600 }}
              >
                {ctaText} <ArrowRightOutlined />
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
